/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Box } from '@mui/material';
import { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  cancelHaloNetSpaceVehiclePolls,
  requestHaloNetSpaceVehicleOrbits,
  requestHaloNetSpaceVehicles,
} from '../../../store/haloNetSpaceVehicles/actions';
import { closeDetailsPanel, closePlatformListDrawer } from '../../../store/ui/actions';
import ErrorBoundary from '../../common/layout/ErrorBoundary';
import TransportMonitoringMapContainer from './TransportMonitoringMapContainer';
import TransportMonitoringPlatformListContainer from './TransportMonitoringPlatformListContainer';
import { selectDetailsPanelOpen, selectSelectedDatapathUuidInPlatformList } from '../../../store';
import { isNil } from 'lodash';
import TailDetailsPanel from '../../fleet/TailDetailsPanel/TailDetailsPanel';
import { DetailsPanelEnum } from '../../../shared/entities/detailsPanel';

const TransportMonitoringContainer = () => {
  // 3rd Party Hooks
  const dispatch = useDispatch();
  const detailsPanelOpen = useSelector(selectDetailsPanelOpen, shallowEqual);
  const selectedSpaceVehicleIdInPlatformList = useSelector(selectSelectedDatapathUuidInPlatformList, shallowEqual);

  // Check if we're in Full Vehicle View mode by checking the route parameter
  const { datapathUuid } = useParams();
  const isFullVehicleView = !isNil(datapathUuid);

  // Poll the HaloNet space vehicle list and the propagated orbits
  useEffect(() => {
    dispatch(requestHaloNetSpaceVehicles());
    dispatch(requestHaloNetSpaceVehicleOrbits());

    return () => {
      dispatch(cancelHaloNetSpaceVehiclePolls());
    };
  }, [dispatch]);

  return (
    <Box>
      <TransportMonitoringMapContainer />
      <ErrorBoundary
        message="Unable to render the Transport Monitoring Platform List Container"
        onReset={() => {
          dispatch(closePlatformListDrawer());
        }}
      >
        <TransportMonitoringPlatformListContainer isInFullVehicleView={isFullVehicleView} />
      </ErrorBoundary>
      <ErrorBoundary
        message="Unable to render the Space Vehicle Details Panel"
        onReset={() => {
          dispatch(closeDetailsPanel());
        }}
      >
        {detailsPanelOpen && !isNil(selectedSpaceVehicleIdInPlatformList) ? (
          <TailDetailsPanel detailsPanelVariant={DetailsPanelEnum.SPACE_VEHICLE} />
        ) : null}
      </ErrorBoundary>
    </Box>
  );
};
export default TransportMonitoringContainer;
